import { language } from "../lang";

/*
    API 오류 인간화 — alertError 에 도달한 원문(프로바이더 JSON 바디, HTTP 상태 텍스트 등)을
    패턴 매칭으로 분류해 "무엇이 문제고 무엇을 하면 되는지"를 사용자 언어로 바꾼다.
    원문은 버리지 않는다 — alertError 가 상세 확장(stackTrace)에 그대로 보존한다.

    보수적 매칭 원칙: alertError 는 앱 내부 오류에도 두루 쓰이므로, API 오류가
    확실한 패턴만 매칭하고 애매하면 null(원문 그대로 표시)을 반환한다.
*/

export type ApiErrorCategory =
    | "auth"
    | "quota"
    | "rateLimit"
    | "overloaded"
    | "contextLength"
    | "modelNotFound"
    | "contentFilter"
    | "serverUnreachable";

export interface HumanizedApiError {
    category: ApiErrorCategory;
    title: string;
    hint: string;
}

// 판정 순서가 곧 우선순위다: OpenAI insufficient_quota 는 429(rate limit)와 함께
// 오므로 quota 를 rateLimit 보다 먼저 둔다.
const MATCHERS: Array<{ category: ApiErrorCategory; patterns: RegExp[] }> = [
    {
        category: "quota",
        patterns: [
            /insufficient_quota/i,
            /exceeded your current quota/i,
            /billing/i,
            /credit balance/i,
            /purchase credits/i,
            /payment required/i,
            /\b402\b/,
        ],
    },
    {
        category: "auth",
        patterns: [
            /invalid[_\s-]?api[_\s-]?key/i,
            /incorrect api key/i,
            /invalid x-api-key/i,
            /authentication[_\s]?error/i,
            /unauthorized/i,
            /api key not valid/i,
            /api_key_invalid/i,
            /permission[_\s]?denied/i,
            /no auth credentials/i,
            /\b401\b/,
            /\b403\b/,
        ],
    },
    {
        category: "contextLength",
        patterns: [
            /context[_\s]?length/i,
            /maximum context/i,
            /context window/i,
            /prompt is too long/i,
            /too many tokens/i,
            /input is too long/i,
        ],
    },
    {
        category: "rateLimit",
        patterns: [
            /rate[_\s]?limit/i,
            /too many requests/i,
            /resource_exhausted/i,
            /quota exceeded/i,
            /\b429\b/,
        ],
    },
    {
        category: "overloaded",
        patterns: [
            /overloaded/i,
            /internal server error/i,
            /server_error/i,
            /server had an error/i,
            /bad gateway/i,
            /service unavailable/i,
            /\bUNAVAILABLE\b/,
            /\b(500|502|503|529)\b/,
        ],
    },
    {
        category: "modelNotFound",
        patterns: [
            /model_not_found/i,
            /model .{0,80}(does not exist|not found|is not supported)/i,
            /(does not exist|not found).{0,80}model/i,
            /unknown model/i,
        ],
    },
    {
        category: "contentFilter",
        patterns: [
            /content_policy/i,
            /content_filter/i,
            /content management policy/i,
            /PROHIBITED_CONTENT/,
            /blocked by .{0,40}safety/i,
            /safety (settings|system|filters)/i,
        ],
    },
    {
        // 자체(인스턴스) 서버나 입력한 엔드포인트에 아예 닿지 못한 경우 —
        // 빈 응답 JSON 파싱 실패(서버 재시작 중)와 연결 거부가 대표 신호다.
        // 프로바이더 API 오류와 겹치지 않도록 맨 마지막에 둔다.
        category: "serverUnreachable",
        patterns: [
            /Unexpected end of JSON input/i,
            /Failed to execute 'json' on 'Response'/i,
            /ECONNREFUSED/,
            /NetworkError when attempting to fetch/i,
        ],
    },
];

const CATEGORY_STRINGS: Record<ApiErrorCategory, () => { title: string; hint: string }> = {
    auth: () => ({ title: language.errors.apiAuth, hint: language.errors.apiAuthHint }),
    quota: () => ({ title: language.errors.apiQuota, hint: language.errors.apiQuotaHint }),
    rateLimit: () => ({ title: language.errors.apiRateLimit, hint: language.errors.apiRateLimitHint }),
    overloaded: () => ({ title: language.errors.apiOverloaded, hint: language.errors.apiOverloadedHint }),
    contextLength: () => ({ title: language.errors.apiContextLength, hint: language.errors.apiContextLengthHint }),
    modelNotFound: () => ({ title: language.errors.apiModelNotFound, hint: language.errors.apiModelNotFoundHint }),
    contentFilter: () => ({ title: language.errors.apiContentFilter, hint: language.errors.apiContentFilterHint }),
    serverUnreachable: () => ({ title: language.errors.apiServerUnreachable, hint: language.errors.apiServerUnreachableHint }),
};

/**
 * 원문 오류 메시지를 분류해 친절한 제목/힌트를 반환한다. 매칭 실패 시 null.
 * 지나치게 긴 메시지는 앞부분만 검사한다(정규식 폭주 방지).
 */
export function humanizeApiError(raw: string): HumanizedApiError | null {
    if (!raw || typeof raw !== "string") return null;
    const probe = raw.length > 4000 ? raw.slice(0, 4000) : raw;

    for (const { category, patterns } of MATCHERS) {
        if (patterns.some(p => p.test(probe))) {
            const { title, hint } = CATEGORY_STRINGS[category]();
            return { category, title, hint };
        }
    }
    return null;
}
